// Names and shapes of the notes corpus: one IndexedDB database per member, two
// object stores, and the scope key the Hub drains under (view plus part).
// docs/plans/2026-09-02-next-steps-e2e-encryption.md, section 6.7; #1548.

export const CORPUS_DB_PREFIX = 'hogc-one-notes-corpus:';
export const CORPUS_DB_VERSION = 1;
export const NOTES_STORE = 'notes';
export const META_STORE = 'meta';

// Every member whose corpus this device opened, so logout can remove every
// corpus without knowing who is signed in (the cached profile is already gone
// by then). Under the next-steps- prefix so clearNextStepsCaches sweeps it too.
export const CORPUS_REGISTRY_KEY = 'next-steps-corpus-members';

export const corpusDbName = memberId => `${CORPUS_DB_PREFIX}${memberId}`;
export const isCorpusDbName = name =>
  typeof name === 'string' &&
  name.startsWith(CORPUS_DB_PREFIX) &&
  name.length > CORPUS_DB_PREFIX.length;

export const normaliseView = view => (view === 'leader' ? 'leader' : 'student');

/** The key a drain is scoped by: the reader view and the Next Steps part. */
export const scopeKey = ({ view, program } = {}) => `${normaliseView(view)}:${program || '*'}`;

/**
 * The scope a note row belongs to, read off the row the API returned: its
 * `audience` (STUDENT | LEADER) is the view, its `programSlug` the part. A row
 * fetched by id during reconciliation is stored under this, not under the
 * scope being reconciled: the manifest is not scoped to a view.
 */
export function noteScopeOf(row, fallback = {}) {
  const audience = row?.audience ? String(row.audience).toUpperCase() : null;
  const view = audience
    ? audience === 'LEADER'
      ? 'leader'
      : 'student'
    : normaliseView(fallback.view);
  const program = row?.programSlug || fallback.program || undefined;
  return { view, program };
}

/** The meta row of a scope before any page has landed. */
export const emptyMeta = scope => ({
  scope: scopeKey(scope),
  view: normaliseView(scope?.view),
  program: scope?.program || null,
  cursor: null,
  total: 0,
  loaded: 0,
  complete: false,
  generation: 0,
});

/** A notes-store record: the raw API row, opaque to this module, plus what it was fetched under. */
export const toRecord = (row, scope) => ({
  id: String(row.id),
  updatedAt: row.updatedAt ?? null,
  view: normaliseView(scope?.view),
  program: scope?.program || null,
  scope: scopeKey(scope),
  row,
});

const readStorage = storage => {
  try {
    const raw = storage?.getItem(CORPUS_REGISTRY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (e) {
    return [];
  }
};

const writeStorage = (storage, ids) => {
  try {
    if (ids.length) storage?.setItem(CORPUS_REGISTRY_KEY, JSON.stringify(ids));
    else storage?.removeItem(CORPUS_REGISTRY_KEY);
  } catch (e) {
    // Storage unavailable: the registry is a fallback for logout, and
    // indexedDB.databases() covers the platforms that matter.
  }
};

export const registry = {
  read: readStorage,
  add(storage, memberId) {
    const ids = readStorage(storage);
    const id = String(memberId);
    if (!ids.includes(id)) writeStorage(storage, [...ids, id]);
  },
  remove(storage, memberId) {
    const id = String(memberId);
    writeStorage(
      storage,
      readStorage(storage).filter(m => m !== id),
    );
  },
  clear(storage) {
    writeStorage(storage, []);
  },
};
